import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const WhatWeBuy = () => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    // Set page title
    document.title = "Industrial Equipment We Buy - Valves Gauges Controls Texas Oklahoma Louisiana | ValveSalesSupply";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete list of industrial equipment we purchase: control valves, pressure gauges, flow meters, temperature instruments, safety equipment, process controls. Serving refineries and petrochemical plants across Texas, Oklahoma, Louisiana, and 6 more states.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'buy industrial valves, purchase surplus gauges, control valve buyers, pressure gauge buyers, flow meter buyers, industrial equipment buyers, process control buyers');
    }

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/what-we-buy');

    // Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Industrial Equipment We Buy - Valves, Gauges, Controls | ValveSalesSupply' },
      { property: 'og:description', content: 'We purchase control valves, pressure gauges, flow meters, temperature instruments, safety equipment, and process controls from refineries and petrochemical plants.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/what-we-buy' },
      { property: 'og:type', content: 'website' }
    ];

    ogTags.forEach(tag => {
      let meta = document.querySelector(`meta[property="${tag.property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', tag.property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', tag.content);
    });

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://valvesalessupply.com/what-we-buy/#webpage",
          "url": "https://valvesalessupply.com/what-we-buy",
          "name": "Industrial Equipment We Purchase - ValveSalesSupply",
          "description": "Complete list of industrial equipment we purchase: control valves, pressure gauges, flow meters, temperature instruments, safety equipment, and process controls.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://valvesalessupply.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "What We Buy",
                "item": "https://valvesalessupply.com/what-we-buy"
              }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": "https://valvesalessupply.com/what-we-buy/#service",
          "name": "Industrial Equipment Purchasing Service",
          "description": "Professional purchasing of surplus industrial valves, instrumentation, and process controls at above scrap market rates.",
          "provider": {
            "@id": "https://valvesalessupply.com/#organization"
          },
          "serviceType": "Industrial Equipment Acquisition",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Equipment Categories We Purchase",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Control Valves",
                  "description": "Ball valves, gate valves, globe valves, check valves, butterfly valves, and control valve assemblies"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Pressure Gauges",
                  "description": "Analog gauges, digital gauges, differential pressure instruments, vacuum gauges, and test gauges"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Process Controls",
                  "description": "PLCs, HMI systems, controllers, transmitters, actuators, and automation equipment"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Flow Meters",
                  "description": "Turbine meters, orifice plates, magnetic meters, ultrasonic meters, and Coriolis meters"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Temperature Instruments",
                  "description": "Thermocouples, RTDs, thermal wells, temperature gauges, and pyrometers"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Safety Equipment",
                  "description": "Relief valves, rupture discs, flame detectors, gas detectors, and emergency shutdown systems"
                }
              }
            ]
          }
        },
        {
          "@type": "ItemList",
          "name": "Industrial Equipment Categories",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "Product",
                "name": "Control Valves",
                "category": "Industrial Valves"
              }
            },
            {
              "@type": "ListItem", 
              "position": 2,
              "item": {
                "@type": "Product",
                "name": "Pressure Gauges",
                "category": "Measurement Instruments"
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "Product",
                "name": "Process Controls",
                "category": "Automation Equipment"
              }
            },
            {
              "@type": "ListItem",
              "position": 4,
              "item": {
                "@type": "Product",
                "name": "Flow Meters",
                "category": "Flow Measurement"
              }
            },
            {
              "@type": "ListItem",
              "position": 5,
              "item": {
                "@type": "Product",
                "name": "Temperature Instruments", 
                "category": "Temperature Measurement"
              }
            },
            {
              "@type": "ListItem",
              "position": 6,
              "item": {
                "@type": "Product",
                "name": "Safety Equipment",
                "category": "Safety Systems"
              }
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What types of valves do you purchase?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We purchase all types of industrial valves including ball valves, gate valves, globe valves, check valves, butterfly valves, plug valves, needle valves, and diaphragm valves in sizes from 1/4 inch to 60 inches in various materials."
              }
            },
            {
              "@type": "Question",
              "name": "Do you buy used or damaged equipment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we purchase equipment in all conditions from excellent to poor. Even damaged or obsolete equipment has value as we can refurbish, sell for parts, or recycle materials. Our extensive network allows us to find buyers for equipment in any condition."
              }
            },
            {
              "@type": "Question",
              "name": "What manufacturers do you accept?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We purchase equipment from all major manufacturers including Emerson Process Management, Honeywell, ABB, Yokogawa, Fisher, Rosemount, and many others. We also buy lesser-known brands if they meet quality standards and have market demand."
              }
            },
            {
              "@type": "Question",
              "name": "Do you purchase complete plant shutdowns?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we handle everything from single items to complete unit shutdowns and full plant closures. We have the capacity, expertise, and resources to manage large-scale equipment removal projects including coordination with your turnaround schedule."
              }
            },
            {
              "@type": "Question",
              "name": "What condition does equipment need to be in?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We purchase equipment in any condition. Working equipment commands premium prices, but we also buy non-functional, damaged, or obsolete equipment. Even equipment destined for scrap has value when sold to the right buyer."
              }
            },
            {
              "@type": "Question",
              "name": "Do you buy equipment that needs calibration?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. Instrumentation and measurement devices that need calibration are still valuable. We have relationships with calibration labs and buyers who specialize in refurbishing instruments to like-new condition."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Brand categories data
  const brandCategories = [
    {
      id: 'valves-controls',
      title: 'VALVES & CONTROLS',
      icon: 'ri-settings-3-line',
      brands: [
        'Q-Check', 'Fisher', 'Masoneilan', 'Valtek', 'Flowseal', 
        'Centerline', 'Relief Valves', 'Rosemount', 'WKM', 
        'General Twinseal', 'Tufline', 'Orbit', 'Durco', 'Edwards'
      ],
      description: 'Premium control valves and valve automation systems from leading manufacturers.',
      serviceAreas: [
        { name: 'Houston', link: '/cities/houston' },
        { name: 'Dallas', link: '/cities/dallas' },
        { name: 'Texas', link: '/states/texas' }
      ]
    },
    {
      id: 'instrumentation',
      title: 'INSTRUMENTATION',
      icon: 'ri-dashboard-line',
      brands: [
        'Rosemount', 'Foxboro', 'Fisher', 'Barton'
      ],
      description: 'Process instrumentation and measurement devices for accurate control and monitoring.',
      serviceAreas: [
        { name: 'Oklahoma City', link: '/cities/oklahoma-city' },
        { name: 'Oklahoma', link: '/states/oklahoma' },
        { name: 'Kansas', link: '/states/kansas' }
      ]
    },
    {
      id: 'flow-measurement',
      title: 'WATER METERS & FLOW',
      icon: 'ri-bar-chart-line',
      brands: [
        'Badger', 'Rockwell', 'Neptune'
      ],
      description: 'Water meters and flow measurement equipment for municipal and industrial applications.',
      serviceAreas: [
        { name: 'Baton Rouge', link: '/cities/baton-rouge' },
        { name: 'Louisiana', link: '/states/louisiana' },
        { name: 'Lake Charles', link: '/cities/lake-charles' }
      ]
    },
    {
      id: 'actuators-positioners',
      title: 'ACTUATORS & POSITIONERS',
      icon: 'ri-cpu-line',
      brands: [
        'Q-Check', 'Fisher', 'Limitorque', 'Masoneilan', 'Valtek', 'Bettis', 'Foxboro'
      ],
      description: 'Valve actuators and positioners for automated valve control systems.',
      serviceAreas: [
        { name: 'Denver', link: '/cities/denver' },
        { name: 'Utah', link: '/states/utah' },
        { name: 'Wyoming', link: '/states/wyoming' }
      ]
    },
    {
      id: 'controllers-regulators',
      title: 'CONTROLLERS & REGULATORS',
      icon: 'ri-settings-4-line',
      brands: [
        'Fisher', 'Q-Check', 'Valtek', 'Conoflow', 'Control Air'
      ],
      description: 'Process controllers and pressure regulators for precise system control.',
      serviceAreas: [
        { name: 'Tulsa', link: '/cities/tulsa' },
        { name: 'Wichita', link: '/cities/wichita' },
        { name: 'Kansas City', link: '/cities/kansas-city' }
      ]
    },
    {
      id: 'transmitters-transducers',
      title: 'TRANSMITTERS & TRANSDUCERS',
      icon: 'ri-radio-line',
      brands: [
        'Fisher', 'Rosemount', 'Masoneilan', 'Valtek', 'Foxboro', 'Taylor'
      ],
      description: 'Signal transmitters and transducers for process communication and control.',
      serviceAreas: [
        { name: 'Bismarck', link: '/cities/bismarck' },
        { name: 'North Dakota', link: '/states/north-dakota' },
        { name: 'Williston', link: '/cities/williston' }
      ]
    },
    {
      id: 'fittings-manifolds',
      title: 'FITTINGS & MANIFOLDS',
      icon: 'ri-links-line',
      brands: [
        'Parker', 'Swagelok', 'Anderson Greenwood', 'Hoke'
      ],
      description: 'High-pressure fittings, manifolds, and connection systems.',
      serviceAreas: [
        { name: 'Casper', link: '/cities/casper' },
        { name: 'Cheyenne', link: '/cities/cheyenne' },
        { name: 'South Dakota', link: '/states/south-dakota' }
      ]
    },
    {
      id: 'specialty-valves',
      title: 'SPECIALTY VALVES',
      icon: 'ri-filter-line',
      brands: [
        'Q-Check', 'Warren', 'CF Fluid Controls', 'Chemvalve', 
        'Flocheck', 'Dafram', 'Duvalco', 'V-turn'
      ],
      description: 'Ball, butterfly, gate, check, block, and lined plug valves for specialized applications.',
      serviceAreas: [
        { name: 'Austin', link: '/cities/austin' },
        { name: 'San Antonio', link: '/cities/san-antonio' },
        { name: 'Corpus Christi', link: '/cities/corpus-christi' }
      ]
    },
    {
      id: 'temperature-gauges',
      title: 'TEMPERATURE & GAUGES',
      icon: 'ri-thermometer-line',
      brands: [
        'WIKA', 'All Temp Sensors', 'OCI', 'MCDaniel'
      ],
      description: 'Thermowells, RTDs, temperature gauges, and precision measurement instruments.',
      serviceAreas: [
        { name: 'Albuquerque', link: '/cities/albuquerque' },
        { name: 'New Mexico', link: '/states/new-mexico' },
        { name: 'El Paso', link: '/cities/el-paso' }
      ]
    },
    {
      id: 'materials-surplus',
      title: 'MATERIALS & SURPLUS',
      icon: 'ri-stack-line',
      brands: [
        'Copper', 'Brass', 'Alloy', 'Stainless Steel', 'Pipe Fittings', 
        'Flanges', 'Motors (All Types)', 'Wire (All Types)'
      ],
      description: 'Scrap metals, electrical surplus, pipe fittings, flanges, motors, and wire.',
      serviceAreas: [
        { name: 'Beaumont', link: '/cities/beaumont' },
        { name: 'Topeka', link: '/cities/topeka' },
        { name: 'Sioux Falls', link: '/cities/sioux-falls' }
      ]
    }
  ];

  const equipmentCategories = [
    {
      title: 'Control Valves',
      icon: 'ri-settings-3-line',
      image: 'https://public.readdy.ai/ai/img_res/e51e907eab22b29059965a5ead518de5.jpg',
      altText: 'Industrial control valves including ball valves, gate valves, and globe valves arranged in warehouse - ValveSalesSupply purchases all types of control valves',
      items: [
        'Ball Valves (2" to 48")',
        'Gate Valves (1" to 36")',
        'Globe Valves (1/2" to 24")',
        'Check Valves (1" to 42")',
        'Butterfly Valves (3" to 60")',
        'Plug Valves (1" to 30")',
        'Needle Valves (1/4" to 2")',
        'Diaphragm Valves (1/2" to 12")'
      ],
      description: 'We purchase all types of industrial valves in various materials including carbon steel, stainless steel, and exotic alloys.'
    },
    {
      title: 'Pressure Instruments',
      icon: 'ri-dashboard-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20pressure%20gauges%20analog%20digital%20meters%20testing%20equipment%20professional%20clean%20background%20industrial%20instrumentation%20organized%20display&width=600&height=400&seq=pressure-instruments-002&orientation=landscape',
      altText: 'Industrial pressure gauges, analog and digital meters, and testing equipment - ValveSalesSupply buys all types of pressure measurement instruments',
      items: [
        'Analog Pressure Gauges',
        'Digital Pressure Gauges',
        'Differential Pressure Gauges',
        'Vacuum Gauges',
        'Test Gauges',
        'Pressure Transmitters',
        'Pressure Switches',
        'Bourdon Tube Gauges'
      ],
      description: 'All pressure measurement devices from standard gauges to high-precision transmitters and testing equipment.'
    },
    {
      title: 'Process Control Systems',
      icon: 'ri-cpu-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20process%20control%20systems%20PLCs%20HMI%20panels%20control%20cabinets%20electronic%20equipment%20professional%20clean%20industrial%20background%20organized%20setup&width=600&height=400&seq=control-systems-003&orientation=landscape',
      altText: 'Industrial process control systems including PLCs, HMI panels, and control cabinets - ValveSalesSupply purchases automation equipment',
      items: [
        'Programmable Logic Controllers (PLCs)',
        'Human Machine Interfaces (HMIs)',
        'Distributed Control Systems (DCS)',
        'Process Controllers',
        'Signal Transmitters',
        'Control Panels',
        'Actuators & Positioners',
        'Loop Controllers'
      ],
      description: 'Complete control systems and individual components from all major manufacturers including Allen-Bradley, Siemens, and Honeywell.'
    },
    {
      title: 'Flow Measurement',
      icon: 'ri-bar-chart-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20flow%20meters%20turbine%20magnetic%20ultrasonic%20orifice%20plates%20professional%20industrial%20equipment%20clean%20background%20organized%20instrumentation&width=600&height=400&seq=flow-meters-004&orientation=landscape',
      altText: 'Industrial flow meters including turbine, magnetic, ultrasonic meters and orifice plates - ValveSalesSupply buys flow measurement equipment',
      items: [
        'Turbine Flow Meters',
        'Magnetic Flow Meters',
        'Ultrasonic Flow Meters',
        'Coriolis Flow Meters',
        'Orifice Plates & Fittings',
        'Vortex Flow Meters',
        'Positive Displacement Meters',
        'Mass Flow Controllers'
      ],
      description: 'All types of flow measurement equipment for liquids, gases, and steam applications across various industries.'
    },
    {
      title: 'Temperature Instruments',
      icon: 'ri-thermometer-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20temperature%20instruments%20thermocouples%20RTDs%20thermal%20wells%20temperature%20gauges%20professional%20clean%20background%20organized%20instrumentation%20display&width=600&height=400&seq=temperature-instruments-005&orientation=landscape',
      altText: 'Industrial temperature instruments including thermocouples, RTDs, thermal wells and temperature gauges - ValveSalesSupply purchases temperature measurement equipment',
      items: [
        'Thermocouples (All Types)',
        'Resistance Temperature Detectors (RTDs)',
        'Thermal Wells',
        'Temperature Transmitters',
        'Temperature Gauges',
        'Pyrometers',
        'Temperature Switches',
        'Calibration Equipment'
      ],
      description: 'Complete range of temperature measurement devices for process monitoring and control applications.'
    },
    {
      title: 'Safety & Relief Equipment',
      icon: 'ri-shield-check-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20safety%20equipment%20relief%20valves%20rupture%20discs%20emergency%20shutdown%20systems%20professional%20clean%20background%20organized%20safety%20instrumentation&width=600&height=400&seq=safety-equipment-006&orientation=landscape',
      altText: 'Industrial safety equipment including relief valves, rupture discs and emergency shutdown systems - ValveSalesSupply purchases safety instrumentation',
      items: [
        'Pressure Relief Valves',
        'Safety Relief Valves',
        'Rupture Discs',
        'Emergency Shutdown Systems',
        'Flame Detectors',
        'Gas Detection Systems',
        'Fire Protection Equipment',
        'Safety Instrumented Systems'
      ],
      description: 'Critical safety equipment for process protection and emergency response systems in industrial facilities.'
    },
    {
      title: 'Analytical Instruments',
      icon: 'ri-microscope-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20analytical%20instruments%20gas%20chromatographs%20analyzers%20laboratory%20equipment%20professional%20clean%20background%20organized%20analytical%20instrumentation&width=600&height=400&seq=analytical-instruments-007&orientation=landscape',
      altText: 'Industrial analytical instruments including gas chromatographs, analyzers and laboratory equipment - ValveSalesSupply buys analytical instrumentation',
      items: [
        'Gas Chromatographs',
        'Process Analyzers',
        'pH Meters & Electrodes',
        'Conductivity Meters',
        'Oxygen Analyzers',
        'Moisture Analyzers',
        'Spectrophotometers',
        'Calibration Standards'
      ],
      description: 'Laboratory and process analytical equipment for quality control and process optimization.'
    },
    {
      title: 'Pumps & Rotating Equipment',
      icon: 'ri-settings-4-line',
      image: 'https://readdy.ai/api/search-image?query=industrial%20pumps%20centrifugal%20positive%20displacement%20rotating%20equipment%20professional%20clean%20background%20organized%20mechanical%20equipment%20warehouse&width=600&height=400&seq=pumps-equipment-008&orientation=landscape',
      altText: 'Industrial pumps including centrifugal and positive displacement pumps and rotating equipment - ValveSalesSupply purchases mechanical equipment',
      items: [
        'Centrifugal Pumps',
        'Positive Displacement Pumps',
        'Gear Pumps',
        'Screw Pumps',
        'Diaphragm Pumps',
        'Magnetic Drive Pumps',
        'Pump Seals & Parts',
        'Motors & Drives'
      ],
      description: 'All types of industrial pumps and rotating equipment including spare parts and accessories.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-24 pb-12 sm:pb-16 bg-gradient-to-b from-[#1B3A5F] to-[#2B4A6F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-white">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">EQUIPMENT WE PURCHASE</h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 max-w-3xl mx-auto">
            We buy surplus industrial equipment from refineries, petrochemical plants, and gas processing facilities. 
            Our expertise covers a wide range of process equipment and instrumentation.
          </p>

          {/* Major Equipment Manufacturer Links */}
          <div className="mt-8 sm:mt-12 text-gray-200 text-sm">
            <p className="mb-4">Equipment from leading manufacturers we purchase:</p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              <a 
                href="https://www.emerson.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-200 hover:text-white underline"
              >
                Emerson Process Management
              </a>
              <a 
                href="https://www.honeywell.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-200 hover:text-white underline"
              >
                Honeywell Process Solutions
              </a>
              <a 
                href="https://www.abb.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-200 hover:text-white underline"
              >
                ABB Measurement & Analytics
              </a>
              <a 
                href="https://www.yokogawa.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-200 hover:text-white underline"
              >
                Yokogawa Electric
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Brand Categories - Tabbed Interface */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">SPECIALIZED BRANDS WE PURCHASE</h2>
            <p className="text-gray-600 text-base sm:text-lg">Premium equipment from industry-leading manufacturers across all major categories</p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
            {brandCategories.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeTab === index
                    ? 'bg-[#1B3A5F] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${category.icon} mr-1 sm:mr-2`} aria-hidden="true"></i>
                {category.title}
              </button>
            ))}
          </div>

          {/* Active Tab Content */}
          <div className="bg-[#F5F7FA] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              
              {/* Left Side - Brand Information */}
              <div>
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="w-12 sm:w-16 h-12 sm:h-16 bg-[#1B3A5F] rounded-xl flex items-center justify-center mr-4">
                    <i className={`${brandCategories[activeTab].icon} text-white text-xl sm:text-2xl`} aria-hidden="true"></i>
                  </div>
                  <h3 className="text-[#1B3A5F] text-xl sm:text-2xl lg:text-3xl font-bold">
                    {brandCategories[activeTab].title}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base leading-relaxed">
                  {brandCategories[activeTab].description}
                </p>

                {/* Brand Grid */}
                <div className="grid sm:grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8">
                  {brandCategories[activeTab].brands.map((brand, brandIndex) => (
                    <div key={brandIndex} className="flex items-center bg-white rounded-lg p-2 sm:p-3 shadow-sm">
                      <i className="ri-check-line text-[#1B3A5F] mr-2 text-sm sm:text-base flex-shrink-0" aria-hidden="true"></i>
                      <span className="text-gray-700 font-medium text-xs sm:text-sm">{brand}</span>
                    </div>
                  ))}
                </div>

                {/* Service Areas */}
                <div className="border-t pt-4 sm:pt-6">
                  <p className="text-gray-600 text-sm mb-3">Service areas for this equipment type:</p>
                  <div className="flex flex-wrap gap-2">
                    {brandCategories[activeTab].serviceAreas.map((area, areaIndex) => (
                      <Link
                        key={areaIndex}
                        to={area.link}
                        className="bg-white text-[#1B3A5F] px-3 py-1 rounded-full text-xs font-medium hover:bg-[#1B3A5F] hover:text-white transition-all duration-300 shadow-sm border border-gray-200"
                      >
                        {area.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Side - Visual Display */}
              <div className="relative">
                <div className="bg-gradient-to-br from-[#1B3A5F] to-[#2B4A6F] rounded-2xl p-6 sm:p-8 text-white text-center">
                  <div className="w-16 sm:w-20 h-16 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <i className={`${brandCategories[activeTab].icon} text-2xl sm:text-3xl`} aria-hidden="true"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Premium Brands</h4>
                  <p className="text-gray-200 text-sm mb-4 sm:mb-6">
                    We purchase {brandCategories[activeTab].brands.length}+ specialized brands in this category
                  </p>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center justify-between bg-white/10 rounded-lg p-2 sm:p-3">
                      <span className="text-xs sm:text-sm">Above Scrap Rates</span>
                      <i className="ri-arrow-up-line text-green-300" aria-hidden="true"></i>
                    </div>
                    <div className="flex items-center justify-between bg-white/10 rounded-lg p-2 sm:p-3">
                      <span className="text-xs sm:text-sm">Fast Pickup</span>
                      <i className="ri-time-line text-blue-300" aria-hidden="true"></i>
                    </div>
                    <div className="flex items-center justify-between bg-white/10 rounded-lg p-2 sm:p-3">
                      <span className="text-xs sm:text-sm">All Conditions</span>
                      <i className="ri-shield-check-line text-yellow-300" aria-hidden="true"></i>
                    </div>
                  </div>

                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/contact"
                      className="bg-white text-[#1B3A5F] px-4 py-2 rounded-full text-sm font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap"
                    >
                      Get Quote
                    </Link>
                    <a
                      href="tel:(214) 854-5638"
                      className="border-2 border-white text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 whitespace-nowrap"
                    >
                      Call Now
                    </a>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-[#1B3A5F]/20 rounded-full"></div>
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-[#1B3A5F]/10 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Quick Contact for Current Category */}
          <div className="mt-8 sm:mt-12 text-center">
            <div className="bg-gradient-to-r from-[#1B3A5F] to-[#2B4A6F] rounded-xl p-4 sm:p-6">
              <p className="text-white text-sm sm:text-base mb-3 sm:mb-4">
                Have {brandCategories[activeTab].title.toLowerCase()} equipment to sell?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:(214) 854-5638"
                  className="bg-white text-[#1B3A5F] px-6 py-2 rounded-full text-sm font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap"
                >
                  Call (214) 854-5638
                </a>
                <a
                  href="mailto:valvesalessupply@gmail.com"
                  className="border-2 border-white text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 whitespace-nowrap"
                >
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Categories */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid gap-8 sm:gap-12 lg:gap-16">
            {equipmentCategories.map((category, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-6 sm:gap-8 lg:gap-12`}>
                <div className="flex-1 w-full">
                  <div className="bg-white rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:mb-6">
                      <div className="w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 bg-[#1B3A5F] rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center mb-3 sm:mb-0 sm:mr-4">
                        <i className={`${category.icon} text-white text-lg sm:text-xl lg:text-2xl`} aria-hidden="true"></i>
                      </div>
                      <h2 className="text-[#1B3A5F] text-xl sm:text-2xl lg:text-3xl font-bold">{category.title}</h2>
                    </div>
                    
                    <p className="text-gray-600 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
                      {category.description}
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
                      {category.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center">
                          <i className="ri-check-line text-[#1B3A5F] mr-2 sm:mr-3 text-sm sm:text-base lg:text-lg flex-shrink-0" aria-hidden="true"></i>
                          <span className="text-gray-700 text-xs sm:text-sm lg:text-base">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 w-full">
                  <div className="rounded-xl sm:rounded-2xl lg:rounded-3xl overflow-hidden shadow-lg">
                    <img 
                      src={category.image}
                      alt={category.altText}
                      className="w-full h-48 sm:h-64 lg:h-80 object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Equipment Standards and Certifications */}
          <div className="mt-12 sm:mt-16 lg:mt-20 text-center border-t pt-12">
            <p className="text-gray-600 mb-6">Industry standards and certifications we recognize:</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <a 
                href="https://www.api.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                API Standards
              </a>
              <a 
                href="https://www.asme.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                ASME Codes
              </a>
              <a 
                href="https://www.isa.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                ISA Standards
              </a>
              <a 
                href="https://www.ieee.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                IEEE Standards
              </a>
              <a 
                href="https://www.ansi.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                ANSI Standards
              </a>
              <a 
                href="https://www.astm.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                ASTM International
              </a>
              <a 
                href="https://www.nist.gov" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                NIST Calibration
              </a>
              <a 
                href="https://www.ul.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                UL Certification
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">INDUSTRIES WE SERVE</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Specialized equipment purchasing for various industrial sectors</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                title: 'Oil Refineries',
                icon: 'ri-oil-line',
                description: 'Complete refinery turnaround support including process units and utilities.'
              },
              {
                title: 'Petrochemical Plants',
                icon: 'ri-flask-line',
                description: 'Chemical processing equipment and specialized instrumentation.'
              },
              {
                title: 'Gas Processing',
                icon: 'ri-fire-line',
                description: 'Natural gas processing facilities and pipeline equipment.'
              },
              {
                title: 'Power Generation',
                icon: 'ri-flashlight-line',
                description: 'Power plant equipment including boiler and turbine instrumentation.'
              }
            ].map((industry, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-12 sm:w-16 lg:w-20 h-12 sm:h-16 lg:h-20 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6">
                  <i className={`${industry.icon} text-white text-xl sm:text-2xl lg:text-3xl`} aria-hidden="true"></i>
                </div>
                <h3 className="text-[#1B3A5F] text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4">{industry.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{industry.description}</p>
              </div>
            ))}
          </div>

          {/* Industry Trade Publications */}
          <div className="mt-12 sm:mt-16 text-center border-t pt-8">
            <p className="text-gray-600 mb-4">Industry resources and publications:</p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <a 
                href="https://www.hydrocarbon-processing.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                Hydrocarbon Processing
              </a>
              <a 
                href="https://www.chemicalprocessing.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                Chemical Processing
              </a>
              <a 
                href="https://www.controlglobal.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                Control Global
              </a>
              <a 
                href="https://www.plantengineering.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1B3A5F] hover:underline text-sm"
              >
                Plant Engineering
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Common questions about equipment we purchase</p>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {[
              {
                question: 'What types of valves do you purchase?',
                answer: 'We purchase all types of industrial valves including ball valves (2" to 48"), gate valves (1" to 36"), globe valves, check valves, butterfly valves, plug valves, needle valves, and diaphragm valves. We accept valves in carbon steel, stainless steel, and exotic alloys.'
              },
              {
                question: 'Do you buy used or damaged equipment?',
                answer: 'Yes, we purchase equipment in all conditions from excellent to poor. Even damaged or obsolete equipment has value as we can refurbish, sell for parts, or recycle materials. Our extensive network allows us to find buyers for equipment in any condition.'
              },
              {
                question: 'What manufacturers do you accept?',
                answer: 'We purchase equipment from all major manufacturers including Emerson Process Management, Honeywell, ABB, Yokogawa, Fisher, Rosemount, and many others. We also buy lesser-known brands if they meet quality standards and have market demand.'
              },
              {
                question: 'Do you purchase complete plant shutdowns?',
                answer: 'Yes, we handle everything from single items to complete unit shutdowns and full plant closures. We have the capacity, expertise, and resources to manage large-scale equipment removal projects including coordination with your turnaround schedule.'
              },
              {
                question: 'What condition does equipment need to be in?',
                answer: 'We purchase equipment in any condition. Working equipment commands premium prices, but we also buy non-functional, damaged, or obsolete equipment. Even equipment destined for scrap has value when sold to the right buyer.'
              },
              {
                question: 'Do you buy equipment that needs calibration?',
                answer: 'Absolutely. Instrumentation and measurement devices that need calibration are still valuable. We have relationships with calibration labs and buyers who specialize in refurbishing instruments to like-new condition.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-[#1B3A5F] text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#1B3A5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 lg:mb-6">HAVE EQUIPMENT TO SELL?</h2>
          <p className="text-gray-200 text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10">
            Get a professional assessment and competitive quote for your surplus equipment.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#1B3A5F] px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg lg:text-xl font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap"
            >
              GET A QUOTE
            </Link>
            <a
              href="tel:(214) 854-5638"
              className="border-2 border-white text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg lg:text-xl font-semibold hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 flex items-center justify-center whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2" aria-hidden="true"></i>
              CALL (214) 854-5638
            </a>
          </div>

          {/* Related Equipment Suppliers */}
          <div className="mt-8 sm:mt-12 border-t border-white/20 pt-8">
            <p className="text-gray-200 mb-4 text-sm">Equipment suppliers and distributors we work with:</p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-xs">
              <a 
                href="https://www.grainger.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white underline"
              >
                Grainger Industrial Supply
              </a>
              <a 
                href="https://www.mscdir.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white underline"
              >
                MSC Industrial Supply
              </a>
              <a 
                href="https://www.fastenal.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white underline"
              >
                Fastenal Industrial
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Fisher%20control%20valves%20with%20Masoneilan%20actuators%20and%20Valtek%20severe%20service%20valves%20arranged%20on%20clean%20white%20surface%20showing%20valve%20bodies%20actuators%20trim%20and%20positioners%20in%20excellent%20condition%20high%20quality%20industrial%20equipment%20photography%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-valves-001&orientation=landscape"
                alt="Used Fisher Control Valves Masoneilan Valves and Valtek Severe Service Valves - Industrial Control Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Valves & Controls We Purchase</h3>
              <p className="text-gray-600 mb-6">
                We buy premium control valves and automation equipment from industry-leading manufacturers. Whether you have surplus Fisher regulators, Masoneilan control valves, or Valtek severe service equipment, we offer competitive pricing and fast pickup across all service areas.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/houston" className="underline hover:text-teal-600">Houston</a>, <a href="/cities/dallas" className="underline hover:text-teal-600">Dallas</a>, <a href="/cities/oklahoma-city" className="underline hover:text-teal-600">Oklahoma City</a>, <a href="/states/texas" className="underline hover:text-teal-600">Texas</a>, <a href="/states/oklahoma" className="underline hover:text-teal-600">Oklahoma</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Control Valves</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Q-Check</strong> - Control valves, check valves</li>
                <li><strong>Fisher</strong> - All control valve models</li>
                <li><strong>Masoneilan</strong> - Control valves, positioners</li>
                <li><strong>Valtek</strong> - Severe service valves</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Specialty Valves</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Flowseal</strong> - Lined butterfly valves</li>
                <li><strong>Centerline</strong> - Butterfly valves</li>
                <li><strong>WKM</strong> - Gate and ball valves</li>
                <li><strong>Edwards</strong> - Relief valves</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Additional Brands</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Rosemount</strong> - Valve systems</li>
                <li><strong>General Twinseal</strong> - Dual seal valves</li>
                <li><strong>Tufline</strong> - Industrial valves</li>
                <li><strong>Orbit</strong> - Ball valves</li>
                <li><strong>Durco</strong> - Process valves</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Get Quote for Your Valves
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Rosemount%20pressure%20transmitters%20and%20Foxboro%20instrumentation%20control%20panel%20with%20Fisher%20controllers%20and%20Barton%20pressure%20instruments%20mounted%20in%20control%20room%20showing%20industrial%20process%20control%20equipment%20with%20digital%20displays%20and%20sensors%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-instrumentation-002&orientation=landscape"
                alt="Used Rosemount Transmitters Foxboro Controllers and Fisher Instrumentation for Sale - Process Control Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Instrumentation Equipment</h3>
              <p className="text-gray-600 mb-6">
                We purchase high-quality process instrumentation from Rosemount, Foxboro, Fisher, and Barton. From pressure transmitters to temperature controllers, we buy all types of industrial instrumentation in any condition.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/dallas" className="underline hover:text-teal-600">Dallas</a>, <a href="/cities/tulsa" className="underline hover:text-teal-600">Tulsa</a>, <a href="/states/louisiana" className="underline hover:text-teal-600">Louisiana</a>, <a href="/cities/baton-rouge" className="underline hover:text-teal-600">Baton Rouge</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Rosemount</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Pressure transmitters</li>
                <li>Temperature sensors</li>
                <li>Flow meters</li>
                <li>Level instruments</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Foxboro</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Controllers</li>
                <li>Recorders</li>
                <li>Transmitters</li>
                <li>Positioners</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Fisher</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Transmitters</li>
                <li>Controllers</li>
                <li>Recorders</li>
                <li>Regulators</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Barton</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Pressure instruments</li>
                <li>Differential pressure</li>
                <li>Chart recorders</li>
                <li>Gauges</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Sell Your Instrumentation
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Badger%20water%20meters%20Neptune%20flow%20meters%20and%20Rockwell%20industrial%20metering%20equipment%20displayed%20on%20clean%20white%20surface%20showing%20digital%20displays%20process%20connections%20and%20mounting%20brackets%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-meters-003&orientation=landscape"
                alt="Used Badger Water Meters Neptune Flow Meters and Rockwell Industrial Meters for Sale - Flow Measurement Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Water Meters & Flow Equipment</h3>
              <p className="text-gray-600 mb-6">
                We buy water meters and flow measurement equipment from Badger, Rockwell, and Neptune. Whether you're upgrading your facility or have surplus inventory, we offer competitive prices for all meter types and sizes.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/houston" className="underline hover:text-teal-600">Houston</a>, <a href="/cities/san-antonio" className="underline hover:text-teal-600">San Antonio</a>, <a href="/cities/oklahoma-city" className="underline hover:text-teal-600">Oklahoma City</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Badger Meters</h4>
              <ul className="space-y-2 text-gray-700">
                <li>Water meters - all sizes</li>
                <li>Flow meters</li>
                <li>Compound meters</li>
                <li>Turbine meters</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Rockwell</h4>
              <ul className="space-y-2 text-gray-700">
                <li>Industrial meters</li>
                <li>Flow measurement</li>
                <li>Metering systems</li>
                <li>Calibration equipment</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Neptune</h4>
              <ul className="space-y-2 text-gray-700">
                <li>Water measurement systems</li>
                <li>Positive displacement meters</li>
                <li>AMR/AMI systems</li>
                <li>Meter registers</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Get Quote for Your Meters
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Limitorque%20electric%20actuators%20Bettis%20pneumatic%20actuators%20and%20Fisher%20valve%20positioners%20assembly%20displayed%20on%20clean%20white%20surface%20showing%20heavy%20duty%20industrial%20valve%20automation%20equipment%20with%20control%20panels%20and%20mounting%20hardware%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-actuators-004&orientation=landscape"
                alt="Used Limitorque Electric Actuators Bettis Pneumatic Actuators and Fisher Positioners for Sale - Valve Automation Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Actuators & Positioners</h3>
              <p className="text-gray-600 mb-6">
                We purchase valve actuators and positioners from Fisher, Limitorque, Bettis, Masoneilan, Valtek, and Foxboro. From electric to pneumatic actuators, we buy all types in working or non-working condition.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/states/texas" className="underline hover:text-teal-600">Texas</a>, <a href="/cities/tulsa" className="underline hover:text-teal-600">Tulsa</a>, <a href="/cities/corpus-christi" className="underline hover:text-teal-600">Corpus Christi</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Electric Actuators</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Limitorque</strong> - All models</li>
                <li><strong>Fisher</strong> - Electric actuators</li>
                <li><strong>Q-Check</strong> - Actuator systems</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Pneumatic Actuators</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Bettis</strong> - All sizes</li>
                <li><strong>Masoneilan</strong> - Pneumatic systems</li>
                <li><strong>Valtek</strong> - Actuators</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Positioners</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Fisher</strong> - All positioner models</li>
                <li><strong>Foxboro</strong> - Smart positioners</li>
                <li><strong>Masoneilan</strong> - Positioners</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Sell Your Actuators
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Rosemount%20pressure%20transmitters%20Fisher%20transducers%20and%20Taylor%20temperature%20transmitters%20mounted%20on%20industrial%20equipment%20showing%20precision%20measurement%20and%20signal%20conversion%20devices%20with%20digital%20displays%20and%20connection%20terminals%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-transmitters-006&orientation=landscape"
                alt="Used Rosemount Transmitters Fisher Transducers and Industrial Signal Converters for Sale - Process Measurement Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Transmitters & Transducers</h3>
              <p className="text-gray-600 mb-6">
                We purchase transmitters and transducers from Rosemount, Foxboro, Fisher, Taylor, Masoneilan, and Valtek. From pressure transmitters to temperature transducers, we buy all types of signal conversion equipment.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/houston" className="underline hover:text-teal-600">Houston</a>, <a href="/states/louisiana" className="underline hover:text-teal-600">Louisiana</a>, <a href="/cities/austin" className="underline hover:text-teal-600">Austin</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Transmitters</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Rosemount</strong> - All transmitter models</li>
                <li><strong>Foxboro</strong> - Smart transmitters</li>
                <li><strong>Fisher</strong> - Transmitters</li>
                <li><strong>Taylor</strong> - Temperature transmitters</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Transducers</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Fisher</strong> - I/P transducers</li>
                <li><strong>Rosemount</strong> - Signal converters</li>
                <li><strong>Masoneilan</strong> - Transducers</li>
                <li><strong>Valtek</strong> - Positioner transducers</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Specialty Equipment</h4>
              <ul className="space-y-2 text-gray-700">
                <li>Pressure transmitters</li>
                <li>Temperature transmitters</li>
                <li>Level transmitters</li>
                <li>Flow transmitters</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Sell Your Transmitters
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20Swagelok%20tube%20fittings%20Parker%20hydraulic%20fittings%20Anderson%20Greenwood%20manifolds%20and%20Hoke%20instrument%20valves%20collection%20displayed%20on%20clean%20white%20surface%20showing%20precision%20stainless%20steel%20compression%20fittings%20and%20connection%20components%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-fittings-007&orientation=landscape"
                alt="Used Swagelok Fittings Parker Tube Fittings and Anderson Greenwood Manifolds for Sale - Industrial Connection Components"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Fittings & Manifolds</h3>
              <p className="text-gray-600 mb-6">
                We buy tube fittings, manifolds, and connection components from Swagelok, Parker, Anderson Greenwood, and Hoke. Whether you have surplus inventory or are upgrading your systems, we offer competitive pricing for all fitting types.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/dallas" className="underline hover:text-teal-600">Dallas</a>, <a href="/cities/san-antonio" className="underline hover:text-teal-600">San Antonio</a>, <a href="/cities/tulsa" className="underline hover:text-teal-600">Tulsa</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Swagelok</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Tube fittings</li>
                <li>Valves</li>
                <li>Regulators</li>
                <li>Quick connects</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Parker</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Hydraulic fittings</li>
                <li>Tube fittings</li>
                <li>Adapters</li>
                <li>Couplings</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Anderson Greenwood</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Manifolds</li>
                <li>Valve systems</li>
                <li>Instrument valves</li>
                <li>Accessories</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Hoke</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Instrument valves</li>
                <li>Fittings</li>
                <li>Manifolds</li>
                <li>Specialty valves</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Get Quote for Fittings
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20industrial%20ball%20valves%20butterfly%20valves%20gate%20valves%20and%20check%20valves%20from%20Q-Check%20Warren%20and%20Chemvalve%20showing%20variety%20of%20specialty%20valve%20types%20including%20lined%20plug%20valves%20in%20different%20sizes%20and%20materials%20on%20clean%20white%20surface%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-specialty-008&orientation=landscape"
                alt="Used Ball Valves Butterfly Valves Gate Valves and Check Valves for Sale - Specialty Industrial Valve Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Specialty Valves</h3>
              <p className="text-gray-600 mb-6">
                We purchase all types of specialty valves including ball, butterfly, gate, check, and lined plug valves from Q-Check, Warren, Chemvalve, CF Fluid Controls, and many other manufacturers.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/houston" className="underline hover:text-teal-600">Houston</a>, <a href="/cities/corpus-christi" className="underline hover:text-teal-600">Corpus Christi</a>, <a href="/cities/beaumont" className="underline hover:text-teal-600">Beaumont</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Ball & Butterfly</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Q-Check</strong> - Ball valves</li>
                <li><strong>Dafram</strong> - Butterfly valves</li>
                <li><strong>CF Fluid Controls</strong> - Ball/butterfly</li>
                <li><strong>Duvalco</strong> - Ball valves</li>
                <li><strong>V-turn</strong> - Ball valves</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Gate & Check</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Q-Check</strong> - Check valves</li>
                <li><strong>Warren</strong> - Gate valves</li>
                <li><strong>CF Fluid Controls</strong> - Gate valves</li>
                <li><strong>Chemvalve</strong> - Gate valves</li>
                <li><strong>Flocheck</strong> - Check valves</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Lined Plug Valves</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Q-Check</strong> - Lined plug valves</li>
                <li><strong>Chemvalve</strong> - Lined plug valves</li>
                <li>All sizes and materials</li>
                <li>Any condition accepted</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Sell Your Specialty Valves
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20WIKA%20pressure%20gauges%20thermowells%20RTDs%20temperature%20sensors%20and%20OCI%20gauges%20collection%20displayed%20on%20clean%20white%20surface%20showing%20precision%20industrial%20measurement%20instruments%20with%20brass%20and%20stainless%20steel%20construction%20showing%20calibration%20dials%20and%20digital%20displays%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-gauges-009&orientation=landscape"
                alt="Used WIKA Gauges Thermowells RTD Sensors and Industrial Temperature Measurement Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Temperature & Gauges</h3>
              <p className="text-gray-600 mb-6">
                We buy pressure gauges, thermowells, RTDs, and temperature sensors from WIKA, All Temp Sensors, OCI, and MCDaniel. Whether you have surplus gauges or are upgrading your measurement systems, we offer competitive pricing.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: <a href="/cities/dallas" className="underline hover:text-teal-600">Dallas</a>, <a href="/cities/oklahoma-city" className="underline hover:text-teal-600">Oklahoma City</a>, <a href="/cities/austin" className="underline hover:text-teal-600">Austin</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">WIKA</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>Pressure gauges</li>
                <li>Temperature gauges</li>
                <li>Thermometers</li>
                <li>Diaphragm seals</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Thermowells</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>All materials</li>
                <li>All sizes</li>
                <li>Flanged</li>
                <li>Threaded</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">RTD Sensors</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>All Temp Sensors</li>
                <li>RTD assemblies</li>
                <li>Temperature probes</li>
                <li>Sensor heads</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Other Brands</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>OCI gauges</li>
                <li>MCDaniel</li>
                <li>All manufacturers</li>
                <li>Any condition</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Get Quote for Gauges
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20industrial%20scrap%20metal%20materials%20including%20copper%20pipes%20brass%20fittings%20stainless%20steel%20components%20alloy%20pieces%20and%20electrical%20surplus%20motors%20with%20wire%20spools%20sorted%20recyclable%20metal%20materials%20and%20electrical%20equipment%20in%20industrial%20warehouse%20showing%20metallic%20textures%20and%20variety%20in%20clean%20background%20with%20studio%20lighting%20and%20neutral%20gray%20background&width=1000&height=750&seq=whatwebuy-materials-010&orientation=landscape"
                alt="Industrial Scrap Metal Copper Brass Stainless Steel Alloy and Electrical Surplus Equipment"
                className="w-full h-80 object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Materials & Surplus</h3>
              <p className="text-gray-600 mb-6">
                We purchase scrap metals, pipe fittings, flanges, electrical surplus, motors, and wire. Whether you're cleaning out a facility or have surplus materials from a project, we offer competitive pricing and fast pickup.
              </p>
              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-teal-800 font-medium">
                  <i className="ri-map-pin-line mr-2"></i>
                  Serving: All service areas - <a href="/service-areas" className="underline hover:text-teal-600">View locations</a>
                </p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Scrap Metals</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Copper</strong> - All grades</li>
                <li><strong>Brass</strong> - All types</li>
                <li><strong>Alloy</strong> - Various alloys</li>
                <li><strong>Stainless Steel</strong> - All grades</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Pipe & Fittings</h4>
              <ul className="space-y-2 text-gray-700">
                <li>Pipe fittings - all sizes</li>
                <li>Flanges - all types</li>
                <li>All materials</li>
                <li>Any condition</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg text-gray-900 mb-3">Electrical Surplus</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Motors</strong> - All types and sizes</li>
                <li><strong>Wire</strong> - All types</li>
                <li>Electrical components</li>
                <li>Control panels</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="/contact" className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap">
              Sell Your Materials
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhatWeBuy;
